<template>
  <div id="js-qubl"></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from "vue"

import { useExitModal } from "@/composables/useExitModal"
import { useUserStore } from "@/stores/user"

import { GlobalFunctions } from "@/helpers/globalFunctions"

// Services
import api from "@/services/apiClient"
import GtmService from "@/services/gtmService"
import QuizService from "@/services/quizService"

// Types
import type QuizPostReportIn from "@/shared/dtos/in/QuizPostReportIn"
import UserPostIn from "@/shared/dtos/in/UserPostIn"
import ReportStatus from "@/shared/enums/ReportStatus"
import type { QuizData } from "@/types/quizData"
import type { ApiError } from "@/shared/apiClient"
import { UserFlow } from "@/services/gtmService"

// EMITS
const emit = defineEmits(["quizCompleted", "quizSuccess", "quizError"])

// STORES
const userStore = useUserStore()

// COMPOSABLES
const { removeExitModal } = useExitModal()

// PROPS

const props = defineProps({
  quizId: {
    type: String,
    required: true
  },
  quizStyle: {
    type: String,
    required: true
  }
})

// EXPOSED METHODS

defineExpose({ processQuizCompletionWithNewEmail })

// DATA

const data = reactive({
  quizData: {} as QuizData
})

// FUNCTIONS (QUBL SETUP)

function listenToQublPostback(quizId: string): void {
  const qublInstance = document.querySelector("qubl-instance")

  if (!qublInstance) {
    console.error("Qubl instance not found")
    return
  }

  qublInstance.addEventListener("create", async () => {
    await GtmService.quizStartEvent(UserFlow.REPORT)
  })

  qublInstance.addEventListener("postback", async (qublResponse: any) => {
    await GtmService.quizCompleteEvent(UserFlow.REPORT)

    data.quizData = qublResponse?.detail
    processQuizCompletion(data.quizData)

    // Remove quiz data in localstorage to avoid quiz retake blank screen
    localStorage.removeItem(quizId)
  })

  qublInstance.addEventListener("next", async () => {
    observeHelperText()
  })

  qublInstance.addEventListener("back", async () => {
    observeHelperText()
  })
}

const observer = ref<MutationObserver | null>(null)

const observeHelperText = () => {
  const qublInstance = document.querySelector("qubl-instance")?.shadowRoot
  const qublQuiz = qublInstance?.querySelector("qubl-quiz")?.shadowRoot

  if (qublQuiz && !observer.value) {
    observer.value = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          const questionContainer = qublQuiz?.querySelector(".question-container")
          const helperText = qublQuiz.querySelector("#helper-text:not(#helper-text.moved)")

          if (helperText) {
            if (questionContainer) {
              helperText.classList.add("moved")
              questionContainer.appendChild(helperText)
            }
          }
        }
      }
    })

    observer.value.observe(qublQuiz, { childList: true, subtree: true })
  }
}

const unobserveHelperText = () => {
  if (observer.value) {
    observer.value.disconnect()
  }
}

// FUNCTIONS (QUIZ COMPLETION)

async function processQuizCompletion(quizData: QuizData): Promise<void> {
  // Event triggers UI update
  emit("quizCompleted")

  // Exit modal no longer needed
  removeExitModal()

  try {
    const { quizUid: qublQuizUuid, instanceUid: qublInstanceUuid, answers, results } = quizData

    let userSource = GlobalFunctions.getUserSourceData() || {}

    userSource["quiz_page"] = window.location.pathname

    // Post new user  to the API
    const newUser: UserPostIn = {
      fullName: answers.fullName,
      email: answers.email,
      phoneNumber: answers.phoneNumber,
      gender: answers.gender,
      zipCode: answers.zipCode,
      userSource,
      smsConsent: !!answers.phoneNumber // if they provide phone in this quiz, they consented to SMS
    }

    await api.user.post(newUser)
    await userStore.loadUserData() // Reload user data to get the new user's id, and send the user data to GTM

    // Remove user variables from answers
    const cleanedAnswers = QuizService.removeUserVariablesFromAnswers(JSON.parse(JSON.stringify(answers)))

    // Post user's quiz data to the API
    const storableData = {
      qublQuizUuid,
      qublInstanceUuid,
      ...QuizService.getRiasecFromResults(results),
      ...cleanedAnswers
    }

    await api.quiz.postReport(storableData as QuizPostReportIn)

    await GtmService.signUpEvent(UserFlow.REPORT)

    userStore.userData.reportStatus = ReportStatus.FINISHED

    emit("quizSuccess", {
      full_name: answers.fullName,
      zip_code: answers.zipCode,
      test_purpose: answers.testTakingPurpose
    })
  } catch (error) {
    emit("quizError", error as ApiError, quizData)
  }
}

function processQuizCompletionWithNewEmail(quizData: QuizData): void {
  data.quizData = quizData
  processQuizCompletion(data.quizData)
}

// Lifecycle
onMounted(() => {
  GlobalFunctions.storeUserSourceData()
  QuizService.loadQublInstance(props.quizId, props.quizStyle)
  listenToQublPostback(props.quizId)
})

onUnmounted(() => {
  unobserveHelperText()
})
</script>
